import { BellOutlined, UserOutlined } from '@ant-design/icons'
import { Button, Card, Input, Radio, Space, Switch, Tag, Typography } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { ButtonCommon, ModalCommon } from '../../common'
import { ListPageIntro, ListPageShell } from '../../common/ListPageLayout/ListPageLayout'
import { TextAreaCommon } from '../../common/TextAreaCommon/TextAreaCommon'
import { useAppDispatch, useAppSelector } from '../../hooks'
import {
	onAddNotification,
	onCancelCustomNotificationSendJob,
	onGetCustomNotificationSendJobStatus,
} from '../../redux/notification/notification.action'
import { showMessageError, showMessageSuccess } from '../../utils/function.utils'
import './NotificationSend.scss'

const { Text } = Typography
const CUSTOM_NOTIFICATION_TYPE = 'CUSTOM_NOTIFICATION'
const JOB_STATUS_COLORS = {
	queued: 'gold',
	running: 'processing',
	completed: 'success',
	failed: 'error',
	cancelled: 'default',
}

export const NotificationSend = () => {
	const dispatch = useAppDispatch()
	const notification = useAppSelector(state => state.notification)

	const [isSubmitLoading, setIsSubmitLoading] = useState(false)
	const [targetType, setTargetType] = useState(1)
	const [userEmail, setUserEmail] = useState('')
	const [contentDefault, setContentDefault] = useState('')
	const [contentVi, setContentVi] = useState('')
	const [contentEn, setContentEn] = useState('')
	const [avatar, setAvatar] = useState('')
	const [isExternalLink, setIsExternalLink] = useState(false)
	const [linkUrl, setLinkUrl] = useState('')
	const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
	const [pendingPayload, setPendingPayload] = useState(null)
	const [confirmViewMode, setConfirmViewMode] = useState('json')
	const [sendJobStatus, setSendJobStatus] = useState(null)
	const [isCancellingJob, setIsCancellingJob] = useState(false)
	const inputRef = useRef(null)
	const pollTimerRef = useRef(null)

	const stopPolling = () => {
		if (pollTimerRef.current) {
			clearTimeout(pollTimerRef.current)
			pollTimerRef.current = null
		}
	}

	const pollSendJobStatus = jobID => {
		dispatch(
			onGetCustomNotificationSendJobStatus(jobID, (isSuccess, rs) => {
				if (!isSuccess) {
					showMessageError(rs)
					stopPolling()
					return
				}
				setSendJobStatus(rs)
				if (rs.status === 'completed') {
					showMessageSuccess(
						`Notification job completed (${rs.result?.notificationsInserted || 0} sent).`
					)
					stopPolling()
					return
				}
				if (rs.status === 'failed') {
					showMessageError(rs.error || 'Notification send job failed.')
					stopPolling()
					return
				}
				if (rs.status === 'cancelled') {
					showMessageSuccess('Notification send job cancelled.')
					stopPolling()
					return
				}

				pollTimerRef.current = setTimeout(() => {
					pollSendJobStatus(jobID)
				}, 2000)
			})
		)
	}

	const onAddNotificationCallback = (isSuccess, rs) => {
		setIsSubmitLoading(false)
		if (isSuccess) {
			if (rs?.jobID) {
				setSendJobStatus(rs)
				showMessageSuccess(
					`Send job queued (${rs.jobID}). Tracking progress now.`
				)
				stopPolling()
				pollSendJobStatus(rs.jobID)
			} else {
				showMessageSuccess('Send notification successfully')
			}

			setContentDefault('')
			setContentVi('')
			setContentEn('')
			setAvatar('')
			setIsExternalLink(false)
			setLinkUrl('')
			setUserEmail('')
			return
		}
		showMessageError(rs)
	}

	useEffect(() => {
		return () => {
			stopPolling()
		}
	}, [])

	const buildPayload = () => {
		const structuredData = {
			content: {
				default: contentDefault.trim(),
				vi: contentVi.trim(),
				en: contentEn.trim(),
			},
			isExternalLink,
			avatar: avatar.trim(),
		}

		const payload = {
			type: CUSTOM_NOTIFICATION_TYPE,
			content: JSON.stringify(structuredData),
		}
		if (isExternalLink && linkUrl.trim() !== '') {
			payload.link = linkUrl.trim()
		}

		if (notification?.id != null) {
			payload.NotificationID = notification.id
		}
		if (targetType === 2) {
			const email = userEmail.trim()
			payload.email = email
			payload.userEmail = email
		}
		return payload
	}

	const validateBeforeSend = () => {
		if (
			contentDefault.trim() === '' &&
			contentVi.trim() === '' &&
			contentEn.trim() === ''
		) {
			showMessageError('Please enter at least one message content field.')
			return
		}
		if (targetType === 2 && userEmail.trim() === '') {
			showMessageError('Please enter user email for specific-user mode.')
			return
		}
		if (isExternalLink) {
			const link = linkUrl.trim()
			if (link === '') {
				showMessageError('Please enter Link URL when External link is enabled.')
				return
			}
			if (!/^https?:\/\//i.test(link)) {
				showMessageError(
					'Link URL must start with http:// or https:// when External link is enabled.'
				)
				return
			}
		}
		return true
	}

	const handleDryRun = () => {
		if (!validateBeforeSend()) return
		const payload = buildPayload()
		showMessageSuccess(
			`Dry run passed. Payload is valid and ready to send (${payload.type}).`
		)
	}

	const openConfirmModal = () => {
		if (!validateBeforeSend()) return
		setPendingPayload(buildPayload())
		setIsConfirmModalVisible(true)
	}

	const closeConfirmModal = () => {
		setIsConfirmModalVisible(false)
		setConfirmViewMode('json')
	}

	const submitConfirmedPayload = () => {
		if (!pendingPayload) return
		setIsSubmitLoading(true)
		dispatch(onAddNotification(pendingPayload, onAddNotificationCallback))
		setIsConfirmModalVisible(false)
		setConfirmViewMode('json')
	}

	const refreshJobStatusNow = () => {
		if (!sendJobStatus?.jobID) return
		stopPolling()
		pollSendJobStatus(sendJobStatus.jobID)
	}

	const cancelJobNow = () => {
		if (!sendJobStatus?.jobID) return
		setIsCancellingJob(true)
		dispatch(
			onCancelCustomNotificationSendJob(sendJobStatus.jobID, (isSuccess, rs) => {
				setIsCancellingJob(false)
				if (!isSuccess) {
					showMessageError(rs)
					return
				}
				setSendJobStatus(rs)
				showMessageSuccess('Cancel request sent successfully.')
				stopPolling()
			})
		)
	}

	const audienceSummary =
		targetType === 2
			? `Specific user: ${userEmail.trim() || '(missing email)'}`
			: 'All users'

	const onRadioChange = e => {
		const next = e.target.value
		setTargetType(next)
		if (next === 2) {
			inputRef.current?.focus()
		}
	}

	const renderJobStatusTag = status => (
		<Tag color={JOB_STATUS_COLORS[status] || 'default'}>
			{(status || 'unknown').toUpperCase()}
		</Tag>
	)

	return (
		<ListPageShell className="notification-send">
			<div className="list-page-bar">
				<ListPageIntro
					icon={BellOutlined}
					title="Send notification"
					subtitle="Create a custom in-app notification for users. This page always sends type CUSTOM_NOTIFICATION."
				/>
			</div>

			<Card className="notification-send__card" bordered>
				<Space direction="vertical" size={16} className="notification-send__form">
					<div>
						<div className="notification-send__label">Notification type</div>
						<Input value={CUSTOM_NOTIFICATION_TYPE} disabled />
					</div>

					<div>
						<div className="notification-send__label">
							Content default
							<span className="notification-send__required">*</span>
						</div>
						<Text type="secondary" className="notification-send__hint">
							Fallback message if language-specific content is unavailable.
						</Text>
						<TextAreaCommon
							showCount={true}
							onChange={setContentDefault}
							value={contentDefault}
							placeholder="Default content"
							maxLength={2000}
						/>
					</div>

					<div>
						<div className="notification-send__label">
							Content (Vietnamese)
							<span className="notification-send__required">*</span>
						</div>
						<TextAreaCommon
							showCount={true}
							onChange={setContentVi}
							value={contentVi}
							placeholder="Vietnamese content"
							maxLength={2000}
						/>
					</div>

					<div>
						<div className="notification-send__label">
							Content (English)
							<span className="notification-send__required">*</span>
						</div>
						<TextAreaCommon
							showCount={true}
							onChange={setContentEn}
							value={contentEn}
							placeholder="English content"
							maxLength={2000}
						/>
					</div>

					<div>
						<div className="notification-send__label">Avatar URL</div>
						<Input
							value={avatar}
							onChange={e => setAvatar(e.target.value)}
							placeholder="https://.../image.png"
						/>
					</div>

					<div className="notification-send__switch-row">
						<div className="notification-send__label">External link</div>
						<Switch
							checked={isExternalLink}
							onChange={checked => {
								setIsExternalLink(checked)
								if (!checked) {
									setLinkUrl('')
								}
							}}
						/>
					</div>

					<div>
						<div className="notification-send__label">
							Link URL
							{isExternalLink && (
								<span className="notification-send__required">*</span>
							)}
						</div>
						<Input
							value={linkUrl}
							onChange={e => setLinkUrl(e.target.value)}
							placeholder="https://example.com/promo"
							disabled={!isExternalLink}
						/>
					</div>

					<div>
						<div className="notification-send__label">Audience</div>
						<Radio.Group onChange={onRadioChange} value={targetType}>
							<Space direction="vertical" size={8}>
								<Radio value={1}>All users</Radio>
								<Radio value={2}>Specific user (by email)</Radio>
							</Space>
						</Radio.Group>
					</div>

					<div>
						<div className="notification-send__label">
							User email
							{targetType === 2 && (
								<span className="notification-send__required">*</span>
							)}
						</div>
						<Input
							ref={inputRef}
							disabled={targetType !== 2}
							value={userEmail}
							onChange={e => setUserEmail(e.target.value)}
							prefix={<UserOutlined className="site-form-item-icon" />}
							placeholder="example@domain.com"
						/>
					</div>

					<div className="notification-send__actions">
						<ButtonCommon
							type="primary"
							onClick={openConfirmModal}
							loading={isSubmitLoading}
						>
							Send notification
						</ButtonCommon>
						<Button onClick={handleDryRun} disabled={isSubmitLoading}>
							Dry run (no send)
						</Button>
						<Button
							onClick={() => {
								setContentDefault('')
								setContentVi('')
								setContentEn('')
								setAvatar('')
								setIsExternalLink(false)
								setLinkUrl('')
								setUserEmail('')
							}}
							disabled={isSubmitLoading}
						>
							Clear form
						</Button>
					</div>
				</Space>
			</Card>
			<ModalCommon
				title="Confirm notification send"
				visible={isConfirmModalVisible}
				handleCancel={closeConfirmModal}
				width={760}
				footer={
					<div className="notification-send__confirm-footer">
						<Button onClick={closeConfirmModal} disabled={isSubmitLoading}>
							Cancel
						</Button>
						<Button
							type="primary"
							danger
							onClick={submitConfirmedPayload}
							loading={isSubmitLoading}
						>
							Confirm send
						</Button>
					</div>
				}
			>
				<div className="notification-send__confirm-meta">
					<div>
						<strong>Type:</strong> {CUSTOM_NOTIFICATION_TYPE}
					</div>
					<div>
						<strong>Audience:</strong> {audienceSummary}
					</div>
				</div>
				<div className="notification-send__confirm-view-toggle">
					<Button
						size="small"
						type={confirmViewMode === 'json' ? 'primary' : 'default'}
						onClick={() => setConfirmViewMode('json')}
					>
						Payload JSON
					</Button>
					<Button
						size="small"
						type={confirmViewMode === 'html' ? 'primary' : 'default'}
						onClick={() => setConfirmViewMode('html')}
					>
						HTML preview
					</Button>
				</div>
				{confirmViewMode === 'html' ? (
					<div className="notification-send__confirm-html-preview">
						{contentEn.trim() || contentVi.trim() || contentDefault.trim() ? (
							<div
								dangerouslySetInnerHTML={{
									__html:
										contentEn.trim() ||
										contentVi.trim() ||
										contentDefault.trim(),
								}}
							/>
						) : (
							<div>—</div>
						)}
					</div>
				) : (
					<pre className="notification-send__preview-json">
						{pendingPayload
							? JSON.stringify(pendingPayload, null, 2)
							: '{}'}
					</pre>
				)}
			</ModalCommon>
			{sendJobStatus && (
				<Card className="notification-send__job-card" bordered>
					<div className="notification-send__job-header">
						<div className="notification-send__label">Last send job</div>
						<div className="notification-send__job-actions">
							<Button
								size="small"
								onClick={refreshJobStatusNow}
								disabled={
									sendJobStatus.status === 'completed' ||
									sendJobStatus.status === 'failed' ||
									sendJobStatus.status === 'cancelled'
								}
							>
								Refresh now
							</Button>
							<Button
								size="small"
								danger
								onClick={cancelJobNow}
								loading={isCancellingJob}
								disabled={
									sendJobStatus.status === 'completed' ||
									sendJobStatus.status === 'failed' ||
									sendJobStatus.status === 'cancelled'
								}
							>
								Cancel job
							</Button>
						</div>
					</div>
					<div className="notification-send__job-grid">
						<div>
							<strong>Job ID:</strong> {sendJobStatus.jobID}
						</div>
						<div>
							<strong>Status:</strong> {renderJobStatusTag(sendJobStatus.status)}
						</div>
						<div>
							<strong>Scope:</strong> {sendJobStatus.scope}
						</div>
						{sendJobStatus.targetEmail ? (
							<div>
								<strong>Target email:</strong> {sendJobStatus.targetEmail}
							</div>
						) : null}
						{sendJobStatus.result ? (
							<>
								<div>
									<strong>Users scanned:</strong>{' '}
									{sendJobStatus.result.usersScanned}
								</div>
								<div>
									<strong>Notifications inserted:</strong>{' '}
									{sendJobStatus.result.notificationsInserted}
								</div>
							</>
						) : null}
						{sendJobStatus.error ? (
							<div className="notification-send__job-error">
								<strong>Error:</strong> {sendJobStatus.error}
							</div>
						) : null}
					</div>
				</Card>
			)}
		</ListPageShell>
	)
}
